import React, { useState } from "react";
import { Button, Col, Form, Row, Modal, Spinner } from "react-bootstrap";
import { FaBackward } from "react-icons/fa";
import { IoMdCheckbox } from "react-icons/io";
import { MdDone } from "react-icons/md";
import { steps } from "./constants/steps";
import { submitBindingDispute } from "./services/bindingDisputeService";

const BindingDispute = ({ setActivePage }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [formData, setFormData] = useState({
    // Profile of Claimant
    date: "",
    place: "",
    claimantName: "",
    respondentName: "",
    address: "",
    city: "",
    zipCode: "",
    country: "",
    phone: "",
    panNo: "",
    email: "",
    natureOfBusiness: "",
    industry: "",
    termsAccepted: false,
    companyRegistrationDoc: null,
    aecciRegNo: "",
    memberType: "",
    filingFee: 0,

    // Details of Dispute
    agreementStartDate: "",
    product: "",
    commercialInvoiceAmount: "",
    disputeStartDate: "",
    amountOfActivity: "",
    quantumOfClaim: "",
    amountInDispute: "",
    disputeDescription: "",
    agreementContract: null,

    // Respondent Details
    respondentCompanyName: "",
    respondentRepresentativeName: "",
    respondentEmail: "",
    respondentAddress: "",
    respondentState: "",
    respondentZipCode: "",
    respondentPanNo: "",
    respondentWebsite: "",
    respondentCountry: "",

    // Required Documents
    arbitrationForm: null,
    correspondenceWithSupplier: null,
    commercialInvoice: null,
    inspectionReports: null,
    correspondenceWithAgent: null,
    correspondenceWithCarrier: null,
    photographs: null,
    legalActionDocuments: null,

    // Calculation of Fees
    notes: "",

    // Fee-related fields
    adminFee: 0,
    effectiveAdminFee: 0,
    arbitratorFee: 0,
    arbitratorFeeScheduleOne: 0,
    arbitratorFeeScheduleTwo: 0,
    arbitratorFeeScheduleThree: 0,
    arbitratorFeeScheduleFinal: 0,
    arbitratorFeePayableNow: 0,
    totalFee: 0,
    selectedFeePlan: "",
    isFlexibleEnabled: false,

    // Flexible plan installments
    firstInstallment: 0,
    secondInstallment: 0,
    thirdInstallment: 0,

    // Standard plan discount
    standardDiscount: 0,
    originalAdminFee: 0,

    // Fee plan declaration
    feePlanDeclarationAccepted: false,
  });

  const validateStep = (stepIndex) => {
    setShowValidation(true);
    if (stepIndex === 0) {
      const requiredFields = [
        "date",
        "place",
        "claimantName",
        "respondentName",
        "address",
        "city",
        "country",
        "phone",
        "email",
        "zipCode",
        "natureOfBusiness",
        "industry",
        "termsAccepted",
      ];
      const isAllFieldsFilled = requiredFields.every((field) => {
        if (field === "termsAccepted") {
          return formData[field] === true;
        }
        return formData[field] && formData[field].trim() !== "";
      });
      const isFileUploaded = formData.companyRegistrationDoc !== null;
      return isAllFieldsFilled && isFileUploaded;
    } else if (stepIndex === 1) {
      const requiredFields = [
        "agreementStartDate",
        "product",
        "commercialInvoiceAmount",
        "disputeStartDate",
        "amountOfActivity",
        "quantumOfClaim",
        "amountInDispute",
        "disputeDescription",
        "respondentCompanyName",
        "respondentEmail",
        "respondentRepresentativeName",
        "respondentAddress",
        "respondentCountry",
        "respondentState",
        "termsAccepted",
      ];
      const isAllFieldsFilled = requiredFields.every((field) => {
        if (field === "termsAccepted") {
          return formData[field] === true;
        }
        return formData[field] && formData[field].trim() !== "";
      });
      const isFileUploaded = formData.agreementContract !== null;
      return isAllFieldsFilled && isFileUploaded;
    } else if (stepIndex === 2) {
      // Required Documents validation
      const requiredDocuments = ["arbitrationForm", "correspondenceWithSupplier", "commercialInvoice"];

      const isAllRequiredDocumentsUploaded = requiredDocuments.every(
        (doc) => formData[doc] !== null && formData[doc] instanceof File
      );

      return isAllRequiredDocumentsUploaded;
    } else if (stepIndex === 3) {
      // Calculation of Fees validation
      const requiredFields = [
        "notes",
        "termsAccepted",
        "memberType",
        "filingFee",
        "adminFee",
        "totalFee",
        "feePlanDeclarationAccepted",
      ];

      const isAllFieldsFilled = requiredFields.every((field) => {
        if (field === "termsAccepted" || field === "feePlanDeclarationAccepted") {
          return formData[field] === true;
        }
        if (field === "filingFee" || field === "adminFee" || field === "totalFee") {
          return formData[field] > 0;
        }
        return formData[field] && formData[field].trim() !== "";
      });

      // Additional validation for fee plan
      const isFeePlanValid =
        !formData.selectedFeePlan ||
        (formData.selectedFeePlan === "STANDARD" && formData.effectiveAdminFee > 0) ||
        (formData.selectedFeePlan === "FLEXIBLE" && formData.firstInstallment > 0);

      // Validate arbitrator fee
      const isArbitratorFeeValid =
        formData.arbitratorFee > 0 ||
        (formData.selectedFeePlan === "FLEXIBLE" && formData.arbitratorFeeScheduleOne > 0);

      return isAllFieldsFilled && isFeePlanValid && isArbitratorFeeValid;
    }

    return true; // For other steps
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setShowValidation(false);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataForApi = new FormData();

      // Add all form data fields
      const dataFields = [
        // Profile of Claimant
        "date",
        "place",
        "claimantName",
        "respondentName",
        "address",
        "city",
        "zipCode",
        "country",
        "phone",
        "email",
        "natureOfBusiness",
        "industry",
        "aecciRegNo",
        "memberType",
        "filingFee",

        // Details of Dispute
        "agreementStartDate",
        "product",
        "commercialInvoiceAmount",
        "disputeStartDate",
        "amountOfActivity",
        "quantumOfClaim",
        "amountInDispute",
        "disputeDescription",
        "respondentCompanyName",
        "respondentEmail",
        "respondentRepresentativeName",
        "respondentAddress",
        "respondentState",
        "respondentCountry",

        // Calculation of Fees
        "notes",
        "adminFee",
        "effectiveAdminFee",
        "arbitratorFee",
        "totalFee",
      ];

      dataFields.forEach((field) => {
        const value = formData[field];
        if (value !== undefined && value !== null) {
          if (typeof value === "boolean") {
            formDataForApi.append(field, value ? "true" : "false");
          } else {
            formDataForApi.append(field, value.toString());
          }
        } else {
          formDataForApi.append(field, "");
        }
      });

      // Add respondentZipCode only if provided
      if (formData.respondentZipCode && formData.respondentZipCode.trim() !== "") {
        formDataForApi.append("respondentZipCode", formData.respondentZipCode);
      }
      // Add respondentPanNo only if provided
      if (formData.respondentPanNo && formData.respondentPanNo.trim() !== "") {
        formDataForApi.append("respondentPanNo", formData.respondentPanNo);
      }

      if (formData.panNo && formData.panNo.trim() !== "") {
        formDataForApi.append("panNo", formData.panNo);
      }


      // Add respondentWebsite only if provided
      if (formData.respondentWebsite && formData.respondentWebsite.trim() !== "") {
        formDataForApi.append("respondentWebsite", formData.respondentWebsite);
      }

      // Add all file uploads
      const fileFields = [
        "companyRegistrationDoc",
        "agreementContract",
        "arbitrationForm",
        "correspondenceWithSupplier",
        "commercialInvoice",
        "inspectionReports",
        "correspondenceWithAgent",
        "correspondenceWithCarrier",
        "photographs",
        "legalActionDocuments",
      ];

      fileFields.forEach((field) => {
        if (formData[field] && formData[field] instanceof File) {
          formDataForApi.append(field, formData[field]);
        }
      });

      // Add fee plan details as structured data
      const feePlanDetails = {
        memberType: formData.memberType,
        filingFee: formData.filingFee,
        totalFee: formData.totalFee,
        quantumOfClaim: formData.quantumOfClaim,
        amountInDispute: formData.amountInDispute,
        calculationDate: new Date().toISOString(),
        adminFee: {
          effective: formData.effectiveAdminFee,
        },
      };

      if (formData.selectedFeePlan && formData.selectedFeePlan.trim() !== "") {
        feePlanDetails.selectedFeePlan = formData.selectedFeePlan;

        if (formData.selectedFeePlan === "FLEXIBLE") {
          feePlanDetails.flexiblePlan = {
            firstInstallment: formData.firstInstallment,
            secondInstallment: formData.secondInstallment,
            thirdInstallment: formData.thirdInstallment,
          };

          feePlanDetails.arbitratorFee = {
            total: formData.arbitratorFee,
            payableNow: formData.arbitratorFeePayableNow,
            schedules: {
              scheduleOne: formData.arbitratorFeeScheduleOne,
              scheduleTwo: formData.arbitratorFeeScheduleTwo,
              scheduleThree: formData.arbitratorFeeScheduleThree,
              scheduleFinal: formData.arbitratorFeeScheduleFinal,
            },
          };
        } else {
          // STANDARD plan: no schedules
          feePlanDetails.arbitratorFee = {
            total: formData.arbitratorFee,
          };
        }
      } else {
        // No plan selected: only include totals
        feePlanDetails.arbitratorFee = {
          total: formData.arbitratorFee,
        };
      }

      formDataForApi.append("feePlanDetails", JSON.stringify(feePlanDetails));

      await submitBindingDispute(formDataForApi);
      setIsFormComplete(true);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error submitting form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create steps with form data
  const stepsWithData = steps.map((step, index) => ({
    ...step,
    content: React.cloneElement(step.content, {
      formData,
      setFormData,
      showValidation: currentStep === index ? showValidation : false,
    }),
  }));

  return (
    <div>
      <div className="mb-4">
        <div className="d-flex gapx-2 py-1 gap-4 rounded justify-content-end align-items-center">
          <Button onClick={() => setActivePage("trackBindingDispute")}>
            Track Your Dispute
          </Button>
          <Button variant="secondary" onClick={() => setActivePage("homePage")}>
            <FaBackward /> Back
          </Button>
        </div>
      </div>
      <div className="border px-5 py-3 mt-3">
        <div className="my-3">
          <h6>
            A party that has not entered into any contract or agreement and has chosen not to
            invoke the model clause of AECCI-IAC may opt for the non-binding dispute
            resolution process.
          </h6>
        </div>
        <Row>
          <Col>
            <div className="d-flex justify-content-center fs-6">
              <Button active>
                <IoMdCheckbox className="fs-5 mx-1" />
                BINDING DISPUTE
              </Button>
            </div>
          </Col>
          <Col className="d-flex justify-content-center fs-6">
            <Button disabled variant="secondary">
              NON BINDING DISPUTE
            </Button>
          </Col>
        </Row>
        <Form>
          <div className="mx-auto mt-5">
            <div className="progress px-1" style={{ height: "3px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${((isFormComplete ? steps.length : currentStep) / steps.length) * 100}%` }}
                aria-valuenow={(isFormComplete ? steps.length : currentStep + 1) * 2}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            <div className="step-container d-flex justify-content-between">
              {stepsWithData.map((step, index) => (
                <div key={index} className="step-item text-center">
                  <div className={`step-circle ${(currentStep > index || isFormComplete) ? "active" : ""}`}>
                    {(currentStep > index || isFormComplete) ? <MdDone /> : index + 1}
                  </div>
                  <div className="step-label">{step.name}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-3 mt-2">
            {stepsWithData[currentStep].content}
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            {currentStep > 0 && (
              <Button variant="secondary" size="sm" onClick={handlePrevious}>
                Previous
              </Button>
            )}
            {currentStep < steps.length - 1 ? (
              <Button variant="primary" size="sm" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button variant="success" size="sm" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            )}
          </div>
        </Form>
      </div>

      {/* Success Modal */}
      <Modal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          setActivePage("trackBindingDispute");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Success!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="fas fa-check-circle text-success" style={{ fontSize: "4rem" }}></i>
            </div>
            <h5>Your dispute has been successfully submitted!</h5>
            <p className="text-muted">We will review your case and get back to you shortly.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSuccessModal(false);
              setActivePage("trackBindingDispute");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BindingDispute;